import store from '@/store'
import * as validation from '@/util/validation'
import * as CommonInquiry from '@/logic/common/CommonInquiry'
import * as apiInquiry from '@/api/inquiry'
import * as common from '@/util/common'

/**
 * 半角→全角自動変換（computedで使用）
 * @param {} key 
 */
export const computedH2Z = (key) => {
  return {
    get: () => {
      return store.state.inquiry[key]
    },
    set: (value) => {
      value = common.convertAsciiH2Z(value)
      value = common.convertKatakanaH2Z(value)
      store.dispatch('inquiry/setValue', {key: key, value: value})
    }
  }
}

/**
 * 全角→半角自動変換（computedで使用）
 * @param {} key 
 */
export const computedZ2H = (key) => {
  return {
    get: () => {
      return store.state.inquiry[key]
    },
    set: (value) => {
      value = common.convertAsciiZ2H(value)
      store.dispatch('inquiry/setValue', {key: key, value: value})
    }
  }
}

/**
 * お見積もり・お問い合わせ画面：住所選択肢表示用のデータをロード
 * @param {*} postnumber 
 */
export const loadAddresses = (group1) => {

  // 入力チェック(郵便番号のみ)
  const isError = CommonInquiry.checkPostnumber(group1)
  if (isError) {
    // 入力エラーの場合
    return
  }

  // 住所取得APIをコール
  const params = {
    postnumber: group1.postnumber
  }

  store.dispatch('common/showLoading')
  apiInquiry.listAddresses(params)
    .then(response => {
      if (response) {
        const { data } = response

        const list = data.list ?? []

        if (list.length === 0) {
          // 住所が見つからなかった場合
          store.dispatch('common/showNotice', { title: 'エラー', text: '郵便番号に対する住所が見つかりませんでした。' })
        } else if (list.length === 1) {
          // 住所が1件見つかった場合
          group1.address = list[0].address ?? ''
          group1.address2 = list[0].address2 ?? ''
        } else {
          // 住所が複数見つかった場合
          group1.duplicatedAddressList = list
          group1.visiblePostnumberDuplicated = true
        }
      }
    })
    .finally(() => {
      store.dispatch('common/hideLoading')
    })
}

/**
 *  選択された職業区分を取得
 * @param {object} group1 
 * @param {string} defaultMessage 値が存在しなかった場合に表示する文字列
 */
export const getSelectedOccupationClassificationName = (group1, defaultMessage) => {
  const occupationClassification = group1.occupation_classification
  let occupationClassificationName = ''
  if (occupationClassification !== '') {
    for (let i = 0; i < group1.occupationClassificationList.length; i++) {
      const id = group1.occupationClassificationList[i].id
      const name = group1.occupationClassificationList[i].name
      if (occupationClassification === id) {
        occupationClassificationName = name
        break
      }
    }
  }
  return occupationClassificationName || defaultMessage
}

/**
 * 業種・職業名の選択肢(プルダウン)用リストを取得
 * @param {object} group1 
 */
export const getOccupationList = (group1) => {
  const occupationClassification = group1.occupation_classification
  let list = []
  if (occupationClassification !== '') {
    for (let i = 0; i < group1.occupationClassificationList.length; i++) {
      const item = group1.occupationClassificationList[i]
      const id = item.id ?? 0
      const occupations = item.occupations ?? []
      if (id === occupationClassification) {
        list = occupations
        break
      } 
    }
  }
  return list
}

/**
 * 選択された業種・職業名を取得
 * @param {object} group1 
 * @param {string} defaultMessage 値が存在しなかった場合に表示する文字列
 */
export const getSelectedOccupationName = (group1, defaultMessage) => {
  const occupationClassification = group1.occupation_classification
  const occupation = group1.occupation
  let occupationName = ''
  if (occupationClassification !== '' && occupation !== '') {
    for (let i = 0; i < group1.occupationClassificationList.length; i++) {
      const item = group1.occupationClassificationList[i]
      const id = item.id ?? ''
      if (id === occupationClassification) {
        const occupations = item.occupations ?? []
        for (let k = 0; k < occupations.length; k++) {
          const id2 = occupations[k].id
          const name2 = occupations[k].name
          if (id2 === occupation) {
            occupationName = name2
            break
          }
        }
      } 
    }
  }
  return occupationName || defaultMessage
}

/**
 * お見積もり・お問い合わせ画面：職業選択肢表示用のデータをロード
 * @param {*} postnumber 
 */
export const loadJobs = (group1) => {
  // 職業一覧APIをコール
  const params = {}
  return apiInquiry.listJobs(params).then(response => {
    if (response) {
      const { data } = response

      group1.occupationClassificationList = data.occupation_classifications ?? []
    }
  })
}

/**
 * 入力チェック（エンコーディングチェック処理付き）
 * （お問い合わせ、お見積もり共通）
 */
export const checkInput = (group1, actionAfterCompletion) => {

  // エラーメッセージを初期化
  group1.error_name = ''
  group1.error_kana = ''
  group1.error_charge_name = ''
  group1.error_department = ''
  group1.error_postnumber = ''
  group1.error_address = ''
  group1.error_address2 = ''
  group1.error_address3 = ''
  group1.error_address4 = ''
  group1.error_email = ''
  group1.error_email_check = ''
  group1.error_occupation_classification = ''
  group1.error_occupation = ''
  group1.error_tel = ''
  group1.error_tel2 = ''
  group1.error_extension = ''
  group1.error_fax = ''
  if ('error_message_type' in group1) {
    group1.error_message_type = ''
  }
  if ('error_message' in group1) {
    group1.error_message = ''
  }
  if ('error_introducer' in group1) {
    group1.error_introducer = ''
  }
  if ('error_packages' in group1) {
    group1.error_packages = ''
  }
  if ('error_note' in group1) {
    group1.error_note = ''
  }

  // リクエストパラメータ生成
  const params = {}

  // 法人名/個人名
  if ('name' in group1) {
    params.name = group1.name
  }

  // 読み仮名
  if ('kana' in group1) {
    params.kana = group1.kana
  }

  // ご担当者名
  if ('charge_name' in group1) {
    params.charge_name = group1.charge_name
  }

  // ご担当部署名
  if ('department' in group1) {
    params.department = group1.department
  }

  // 住所　市区郡
  if ('address' in group1) {
    params.address = group1.address
  }

  // 住所　町村域大字
  if ('address2' in group1) {
    params.address2 = group1.address2
  }

  // 住所　小字以下番地
  if ('address3' in group1) {
    params.address3 = group1.address3
  }

  // 住所　マンション・ビル名等
  if ('address4' in group1) {
    params.address4 = group1.address4
  }

  // 顧客基本情報文字エンコーディングチェックAPIをコール

  apiInquiry.checkEncoding(params)
    .then(response => {
      if (response) {

        const { data } = response
        
        if (data.status === 0 || data.status === 400) { // エラーの場合は400

          // 入力エラーチェック処理
          let isError = false

          if (typeof data.validation_message === 'undefined') {
            data.validation_message = {} // undefinedエラー回避用(念のための処理)
          }

          // 法人名/個人名
          if ('name' in group1) {
            if (group1.name === '') {
              group1.error_name = '必須項目です。'
              isError = true
            } else if (typeof data.validation_message.name !== 'undefined' && data.validation_message.name.length > 0) {
              group1.error_name = data.validation_message.name[0]
              isError = true
            } else if (!validation.checkMaxLength(group1.name, 50)) {
              group1.error_name = '全角50文字以内で入力してください。'
              isError = true
            }
          }

          // 読み仮名
          if ('kana' in group1) {
            if (group1.kana === '') {
              group1.error_kana = '必須項目です。'
              isError = true
            } else if (typeof data.validation_message.kana !== 'undefined' && data.validation_message.kana.length > 0) {
              group1.error_kana = data.validation_message.kana[0]
              isError = true
            } else if (!validation.checkMaxLength(group1.kana, 60)) {
              group1.error_kana = '全角60文字以内で入力してください。'
              isError = true
            } else if (!validation.checkZenkakuKatakana(group1.kana)) {
              group1.error_kana = '全角カタカナで入力してください。'
              isError = true
            }
          }

          // ご担当者名
          if ('charge_name' in group1) {
            if (group1.charge_name === '') {
              group1.error_charge_name = '必須項目です。'
              isError = true
            } else if (typeof data.validation_message.charge_name !== 'undefined' && data.validation_message.charge_name.length > 0) {
              group1.error_charge_name = data.validation_message.charge_name[0]
              isError = true
            } else if (!validation.checkMaxLength(group1.charge_name, 20)) {
              group1.error_charge_name = '全角20文字以内で入力してください。'
              isError = true
            }
          }

          // ご担当部署名
          if ('department' in group1) {
            if (typeof data.validation_message.department !== 'undefined' && data.validation_message.department.length > 0) {
              group1.error_department = data.validation_message.department[0]
              isError = true
            } else if (!validation.checkMaxLength(group1.department, 20)) {
              group1.error_department = '全角20文字以内で入力してください。'
              isError = true
            }
          }

          // 住所　郵便番号
          if ('postnumber' in group1) {
            if (group1.postnumber === '') {
              group1.error_postnumber = '必須項目です。'
              isError = true
            } else if (!validation.checkHankakuNumber(group1.postnumber) || !validation.checkLength(group1.postnumber, 7)) {
              group1.error_postnumber = '半角数字7桁で入力してください。'
              isError = true
            }
          }

          // 住所　市区郡
          if ('address' in group1) {
            if (group1.address === '') {
              group1.error_address = '必須項目です。'
              isError = true
            } else if (typeof data.validation_message.address !== 'undefined' && data.validation_message.address.length > 0) {
              group1.error_address = data.validation_message.address[0]
              isError = true
            } else if (!validation.checkMaxLength(group1.address, 14)) {
              group1.error_address = '全角14文字以内で入力してください。'
              isError = true
            }
          }

          // 住所　町村域大字
          if ('address2' in group1) {
            if (group1.address2 === '') {
              group1.error_address2 = '必須項目です。'
              isError = true
            } else if (typeof data.validation_message.address2 !== 'undefined' && data.validation_message.address2.length > 0) {
              group1.error_address2 = data.validation_message.address2[0]
              isError = true
            } else if (!validation.checkMaxLength(group1.address2, 14)) {
              group1.error_address2 = '全角14文字以内で入力してください。'
              isError = true
            }
          }

          // 住所　小字以下番地
          if ('address3' in group1) {
            if (group1.address3 === '') {
              group1.error_address3 = '必須項目です。'
              isError = true
            } else if (typeof data.validation_message.address3 !== 'undefined' && data.validation_message.address3.length > 0) {
              group1.error_address3 = data.validation_message.address3[0]
              isError = true
            } else if (!validation.checkMaxLength(group1.address3, 14)) {
              group1.error_address3 = '全角14文字以内で入力してください。'
              isError = true
            }
          }

          // 住所　マンション・ビル名等
          if ('address4' in group1) {
            if (typeof data.validation_message.address4 !== 'undefined' && data.validation_message.address4.length > 0) {
              group1.error_address4 = data.validation_message.address4[0]
              isError = true
            } else if (!validation.checkZenkaku(group1.address4) || !validation.checkMaxLength(group1.address4, 28)) {
              group1.error_address4 = '全角28文字以内で入力してください。'
              isError = true
            }
          }

          // メールアドレス
          if ('email' in group1) {
            if (group1.email === '') {
              group1.error_email = '必須項目です。'
              isError = true
            } else if (!validation.checkHankaku(group1.email) || !validation.checkMaxLength(group1.email, 50)) {
              group1.error_email = '半角50文字以内で入力してください。'
              isError = true
            } else if (!validation.checkEmailFormat(group1.email)) {
              group1.error_email = '正しいメールアドレスを入力してください。'
              isError = true
            }
          }

          // 確認用メールアドレス
          if ('email' in group1 && 'email_check' in group1) { // group1の要素にemailとemail_checkが存在する場合
            if (group1.email_check === '') {
              group1.error_email_check = '必須項目です。'
              isError = true
            } else if (!validation.checkHankaku(group1.email_check) || !validation.checkMaxLength(group1.email_check, 50)) {
              group1.error_email_check = '半角50文字以内で入力してください。'
              isError = true
            } else if (!validation.checkEmailFormat(group1.email_check)) {
              group1.error_email_check = '正しいメールアドレスを入力してください。'
              isError = true
            } else if (group1.email !== group1.email_check) {
              group1.error_email_check = 'メールアドレスが一致していません。'
              isError = true
            }
          }

          // 職業　区分
          if ('occupation_classification' in group1) {
            if (group1.occupation_classification === '') {
              group1.error_occupation_classification = '必須項目です。'
              isError = true
            }
          }

          // 職業　業種・職業
          if ('occupation' in group1) {
            if (group1.occupation === '') {
              group1.error_occupation = '必須項目です。'
              isError = true
            }
          }

          // 電話番号１
          if ('tel' in group1) {
            if (group1.tel === '') {
              group1.error_tel = '必須項目です。'
              isError = true
            } else if (!validation.checkTelFormat(group1.tel)) {
              group1.error_tel = '半角数字・ハイフン13桁以内で正しく入力してください。'
              isError = true
            }
          }

          // 電話番号２
          if ('tel2' in group1) {
            if (group1.tel2 !== '') {
              if (!validation.checkTelFormat(group1.tel2)) {
                group1.error_tel2 = '半角数字・ハイフン13桁以内で正しく入力してください。'
                isError = true
              }
            }
          }

          // 内線番号
          if ('extension' in group1) {
            if (group1.extension !== '') {
              if (!validation.checkExtensionFormat(group1.extension)) {
                group1.error_extension = '半角数字5文字以内で入力してください。'
                isError = true
              }
            } 
          }

          // FAX番号
          if ('fax' in group1) {
            if (group1.fax !== '') {
              if (!validation.checkTelFormat(group1.fax)) {
                group1.error_fax = '半角数字・ハイフン13桁以内で正しく入力してください。'
                isError = true
              }
            }
          }

          // --- 無料トライアルorお見積もり用 ----------------------------------------------
          // 無料トライアルorお見積もり：紹介社員名
          if ('introducer' in group1) {
            if (!validation.checkZenkaku(group1.introducer) || !validation.checkMaxLength(group1.introducer, 10)) {
              group1.error_introducer = '全角10文字以内で入力してください。'
              isError = true
            }
          }

          // 無料トライアルorお見積もり：備考
          if ('note' in group1) {
            if (!validation.checkMaxLength(group1.note, 3000)) {
              group1.error_note = '3,000文字以内で入力してください。'
              isError = true
            }
          }
          
          // --- 無料トライアル用 ----------------------------------------------
          // 無料トライアル：パッケージ、商品
          if ('selectedPackages' in group1) {
            if (group1.selectedPackages.length <= 0) {
              group1.error_packages = 'いずれかのパッケージを選択してください。'
            }
          }

          // --- お問い合わせ用 ----------------------------------------------
          // お問い合わせ：お問い合わせタイプ
          if ('message_type' in group1) {
            if (group1.message_type === '' || group1.message_type === '選択してください') {
              group1.error_message_type = '必須項目です。'
              isError = true
            }
          }

          // お問い合わせ：お問い合わせ内容
          if ('message' in group1) {
            if (group1.message === '') {
              group1.error_message = 'お問い合わせ内容を入力してください。'
              isError = true
            } else if (!validation.checkMaxLength(group1.message, 3000)) {
              group1.error_message = '3,000文字以内で入力してください。'
              isError = true
            }
          }

          actionAfterCompletion(group1, isError)
        }
      }
    })

}

/**
 * 入力チェック(郵便番号のみ)
 * （お問い合わせ、お見積もり共通）
 */
export const checkPostnumber = (group1) => {

  let isError = false

  // エラーメッセージを初期化
  group1.error_postnumber = ''

  // 住所　郵便番号
  if ('postnumber' in group1) {
    if (group1.postnumber === '') {
      group1.error_postnumber = '必須項目です。'
      isError = true
    } else if (!validation.checkHankakuNumber(group1.postnumber) || !validation.checkLength(group1.postnumber, 7)) {
      group1.error_postnumber = '半角数字7桁で入力してください。'
      isError = true
    }
  }

  return isError
}
